import { type IObservableArray, runInAction, when } from 'mobx';

import type {
  Campaign,
  ITradedeskTargetInterest,
  Targetable,
  Targetables as TargetablesCollection,
  Targeting,
  Targetings as TargetingsCollection,
  TPredicateMode,
} from '@feathr/blackbox';

import { determineInterestsMatchMode } from './Interests/Interests.utils';

export function getGeoFilters(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
): Targetable[] {
  return targetings
    .filter((t) => t.get('kind') === 'geo' && !!t.get('target_data') && !t.get('is_archived'))
    .map((t) => Targetables.get(t.get('target_data')!))
    .filter((f) => f.isEphemeral);
}

export function getAgeDemographicTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter(
    (t) => t.get('kind') === 'age_demographic' && !!t.get('target_data') && !t.get('is_archived'),
  );
}

export function getInterestsTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter(
    (t) => t.get('kind') === 'interest' && !!t.get('target_data') && !t.get('is_archived'),
  );
}

export function getGeoTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter(
    (t) => t.get('kind') === 'geo' && !!t.get('target_data') && !t.get('is_archived'),
  );
}

export function getNonArchivedTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter((t) => !t.get('is_archived'));
}

export function getOmniAwarenessTargeting(
  targetings: IObservableArray<Targeting>,
): Targeting | undefined {
  return targetings.find(
    (targeting) => targeting.get('kind') === 'omni_awareness' && !targeting.get('is_archived'),
  );
}

export function getOmniLookalikeTargeting(
  targetings: IObservableArray<Targeting>,
): Targeting | undefined {
  return targetings.find(
    (targeting) => targeting.get('kind') === 'omni_lookalike' && !targeting.get('is_archived'),
  );
}

interface IGetTargetingProps {
  targetings: IObservableArray<Targeting>;
  kinds: string[];
  isArchived?: boolean;
  campaign: Campaign;
}
export function getTargetingByKind({
  kinds,
  targetings,
  campaign,
}: IGetTargetingProps): Targeting | undefined {
  return targetings.find(
    (targeting) =>
      kinds.includes(targeting.get('kind')) &&
      targeting.get('parent') === campaign.get('id') &&
      // Include is_archived === undefined for ephemeral targetings
      targeting.get('is_archived') !== true &&
      !!targeting.get('target_data'),
  );
}

// Helper function to wait for targetables to load
export async function waitForTargetablesToLoad(targetablesToWaitFor: Targetable[]): Promise<void> {
  if (targetablesToWaitFor.length > 0) {
    await Promise.all(targetablesToWaitFor.map((targetable) => when(() => !targetable.isPending)));
  }
}

// Get targetables from a list of targetings
function getTargetablesFromTargetings(
  targetingList: Targeting[],
  Targetables: TargetablesCollection,
): Targetable[] {
  return targetingList
    .map((t) => {
      const targetData = t.get('target_data');
      if (!targetData) {
        return undefined;
      }
      return Targetables.get(targetData);
    })
    .filter((t): t is Targetable => t !== undefined);
}

function getTargetDataIdsFromTargetings(targetings: Targeting[]): string[] {
  return targetings
    .map((t) => t.get('target_data'))
    .filter((t): t is string => typeof t === 'string');
}

// Extract geo filter IDs from targetables
function extractGeoFilterIds(targetables: Targetable[]): string[] {
  return targetables
    .map((targetable) => targetable.get('geo_filter'))
    .filter((id): id is string => typeof id === 'string');
}

// Extract interest filter IDs from targetables
function extractInterestFilterIds(ids: string[], targetables: Targetable[]): string[] {
  return ids
    .map((id) => {
      const targetable = targetables.find((t) => t.id === id);

      if (!targetable) {
        return undefined;
      }

      return (targetable.get('interest') as ITradedeskTargetInterest).id;
    })
    .filter((id): id is string => id !== undefined);
}

// Extract age category filter IDs from targetables
function extractAgeCategoryFilterIds(targetables: Targetable[]): string[] {
  return targetables
    .map((targetable) => targetable.get('age_category'))
    .filter((ageCategory): ageCategory is string => typeof ageCategory === 'string');
}

// Get geo targetings as array of objects with target_data and included
export function getGeoTargetingsArray(
  targetings: IObservableArray<Targeting>,
): Array<{ target_data: string; included: boolean }> {
  return getGeoTargetings(targetings)
    .map((t) => ({
      target_data: t.get('target_data') as string,
      included: t.get('included') === true,
    }))
    .filter((t): t is { target_data: string; included: boolean } => !!t.target_data);
}

/**
 * Get interest IDs from the targetables. Must await loading of targetables.
 * @param targetings - The targetings to get the interest IDs from
 * @param Targetables - The targetables to get the interest IDs from
 * @returns The interest IDs and the match mode
 */
export function getInterestIds(
  targetings: IObservableArray<Targeting>,
  targetables: Targetable[],
): { interestIds: string[]; matchMode: TPredicateMode } {
  // Get just the interest targetings
  const interestTargetings = getInterestsTargetings(targetings);
  // Get the targetable ids from the interest targetings
  const interestTargetDataIds = getTargetDataIdsFromTargetings(interestTargetings);
  // Use the targetables to get the interest ids
  const targetableInterestIds = extractInterestFilterIds(interestTargetDataIds, targetables);

  return {
    interestIds: targetableInterestIds,
    matchMode: determineInterestsMatchMode(interestTargetings),
  };
}

/**
 * Get included and excluded geo filter IDs from the targetables. Must await loading of targetables.
 * @param targetings - The targetings to get the geo filter IDs from
 * @param Targetables - The targetables to get the geo filter IDs from
 * @returns The included and excluded geo filter IDs
 * { included: string[], excluded: string[] }
 */
export async function getGeoFilterIds(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
): Promise<{ included: string[]; excluded: string[] }> {
  const geoTargetings = getGeoTargetings(targetings);

  const includedGeoTargetings = geoTargetings.filter((t) => t.get('included') === true);
  const excludedGeoTargetings = geoTargetings.filter((t) => t.get('included') === false);

  const includedGeoTargetables = getTargetablesFromTargetings(includedGeoTargetings, Targetables);
  const excludedGeoTargetables = getTargetablesFromTargetings(excludedGeoTargetings, Targetables);

  await waitForTargetablesToLoad([...includedGeoTargetables, ...excludedGeoTargetables]);

  return {
    included: extractGeoFilterIds(includedGeoTargetables),
    excluded: extractGeoFilterIds(excludedGeoTargetables),
  };
}

/**
 * Get age category IDs from the targetables. Must await loading of targetables.
 * @param targetings - The targetings to get the age category IDs from
 * @param Targetables - The targetables to get the age category IDs from
 * @returns The age category IDs as an array of strings
 */
export async function getAgeCategoryIds(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
): Promise<string[]> {
  const ageCategoryTargetings = getAgeDemographicTargetings(targetings);
  const ageCategoryTargetables = getTargetablesFromTargetings(ageCategoryTargetings, Targetables);

  await waitForTargetablesToLoad([...ageCategoryTargetables]);

  const ageCategoryIds = extractAgeCategoryFilterIds(ageCategoryTargetables);

  return ageCategoryIds;
}

interface ICreateTargeting {
  campaign: Campaign;
  Targetings: TargetingsCollection;
  targetableId: string;
  kind: string;
  targetings: IObservableArray<Targeting>;
}

export function createTargeting({
  targetableId,
  Targetings,
  kind,
  campaign,
  targetings,
}: ICreateTargeting): Targeting {
  const model = Targetings.create({
    parent: campaign.get('id'),
    kind,
    included: true,
  });

  runInAction(() => {
    // Make it dirty by setting after creation
    model.set({
      target_data: targetableId,
    });

    // Add it to the targetings array
    targetings.push(model);
  });

  return model;
}
